import { BACKEND_URL } from "@/lib/backendUrl";
import { useCallback, useEffect, useState } from "react";
import { Copy, Download, Loader2, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Chip, Panel } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";

const API_BASE = `${BACKEND_URL}/api`;
const pretty = (s) => String(s || "").slice(0, 16).replace("T", " ");

const F = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11.5px] text-muted-foreground">{label}</Label>
    {children}
  </div>
);

export const Bulletin = ({ electionRef }) => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const res = await api.get(`/elections/${electionRef}/bulletin`);
    setData(res.data);
    setDraft(res.data.bulletin);
  }, [electionRef]);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const run = async (key, fn, message) => {
    setBusy(key);
    try {
      await fn();
      toast.success(message);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${what} copied`);
    } catch { toast.error("Your browser would not let us copy that"); }
  };

  if (!data) return null;
  if (!data.declared) {
    return (
      <Panel title="Results bulletin" testId="panel-bulletin-none">
        <p className="text-[12.5px] text-muted-foreground" data-testid="bulletin-not-declared">
          A bulletin is written once the result has been declared.
        </p>
      </Panel>
    );
  }

  const card = `${API_BASE}/public/election-results/${electionRef}/card.png`;
  const share = `${window.location.origin}${data.share_link}`;
  const resultsPage = `${window.location.origin}${data.results_link || ""}`;

  return (
    <Panel title="Results bulletin" testId="panel-bulletin"
           kicker={draft ? `Drafted ${pretty(draft.drafted_at)} · ${draft.source}`
             : "A note for members and donors, and a card worth sharing"}
           actions={data.may_write && (
             <div className="flex flex-wrap items-center gap-2">
               {draft?.status === "sent" && (
                 <Chip testId="bulletin-sent"
                       className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
                   sent to {draft.sent_to}
                 </Chip>
               )}
               <Button size="sm" variant="outline" className="h-8 rounded-full"
                       data-testid="draft-bulletin" disabled={busy === "draft"}
                       onClick={() => run("draft",
                         () => api.post(`/elections/${electionRef}/bulletin`),
                         "A draft is ready — read it before you send it")}>
                 {busy === "draft" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                   : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}
                 {draft ? "Draft it again" : "Draft the bulletin"}
               </Button>
               {draft && (
                 <>
                   <Button size="sm" variant="outline" className="h-8 rounded-full"
                           data-testid="save-bulletin" disabled={busy === "save"}
                           onClick={() => run("save",
                             () => api.put(`/elections/${electionRef}/bulletin`, {
                               headline: draft.headline, members_note: draft.members_note,
                               donors_note: draft.donors_note,
                               social_caption: draft.social_caption }),
                             "Your wording is saved")}>
                     Save my wording
                   </Button>
                   <Button size="sm" className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                           data-testid="send-bulletin" disabled={busy === "send"}
                           onClick={() => run("send",
                             () => api.post(`/elections/${electionRef}/bulletin/send`),
                             "The bulletin has gone to everyone on the roll")}>
                     <Send className="mr-1.5 h-3.5 w-3.5" /> Send to members
                   </Button>
                 </>
               )}
             </div>
           )}>
      <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
        <div className="space-y-3">
          {!draft ? (
            <p className="text-[12.5px] text-muted-foreground" data-testid="bulletin-empty">
              Nothing drafted yet. The wording is written from the certified figures — no number is
              invented — and you can edit every word before it goes out.
            </p>
          ) : (
            <>
              <F label="Headline">
                <Input data-testid="bulletin-headline" value={draft.headline || ""}
                       readOnly={!data.may_write}
                       onChange={(e) => setDraft({ ...draft, headline: e.target.value })} />
              </F>
              <F label="For members">
                <Textarea rows={7} data-testid="bulletin-members" value={draft.members_note || ""}
                          readOnly={!data.may_write}
                          onChange={(e) => setDraft({ ...draft, members_note: e.target.value })} />
              </F>
              <F label="For donors">
                <Textarea rows={4} data-testid="bulletin-donors" value={draft.donors_note || ""}
                          readOnly={!data.may_write}
                          onChange={(e) => setDraft({ ...draft, donors_note: e.target.value })} />
              </F>
              <F label="Caption under the card">
                <Textarea rows={2} data-testid="bulletin-caption"
                          value={draft.social_caption || ""} readOnly={!data.may_write}
                          onChange={(e) => setDraft({ ...draft,
                            social_caption: e.target.value })} />
              </F>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="ghost" className="h-8 rounded-full"
                        data-testid="copy-members"
                        onClick={() => copy(draft.members_note, "The members' note")}>
                  <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy the members' note
                </Button>
                <Button size="sm" variant="ghost" className="h-8 rounded-full"
                        data-testid="copy-donors"
                        onClick={() => copy(draft.donors_note, "The donors' note")}>
                  <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy the donors' note
                </Button>
              </div>
            </>
          )}
        </div>

        <div className="space-y-2">
          <p className="kicker">The shareable card</p>
          <img alt="Election result card" src={card} data-testid="bulletin-card"
               className="w-full rounded-xl border border-border" />
          <div className="flex flex-wrap gap-2">
            <a href={card} download target="_blank" rel="noreferrer" data-testid="download-card">
              <Button size="sm" variant="outline" className="h-8 rounded-full">
                <Download className="mr-1.5 h-3.5 w-3.5" /> Download the card
              </Button>
            </a>
            <Button size="sm" variant="ghost" className="h-8 rounded-full"
                    data-testid="copy-share-link"
                    onClick={() => copy(share, "The shareable link")}>
              <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy the shareable link
            </Button>
            <a href={resultsPage} target="_blank" rel="noreferrer" data-testid="open-public-page">
              <Button size="sm" variant="ghost" className="h-8 rounded-full">
                Open the public page
              </Button>
            </a>
          </div>
          <p className="text-[10.5px] leading-relaxed text-muted-foreground">
            The shareable link unfurls with this card and the caption wherever you paste it, and
            opens the full certified result — no sign-in needed.
          </p>
        </div>
      </div>
    </Panel>
  );
};
